import { ArrowSquareOut, Trash } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Streamdown } from "streamdown";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { BrandInput } from "@/components/brand/brand-input";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardContent,
	DashboardHeaderSection,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useProDialog } from "@/components/ProDialog";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import { db } from "@/lib/instantdb";
import { getPlanCapabilities } from "@/lib/plans";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/listening")({
	component: ListeningPage,
});

const PLATFORM_LABELS: Record<string, string> = {
	reddit: "Reddit",
	bluesky: "Bluesky",
	youtube: "YouTube",
};

function formatRelativeTime(
	value: string | number | Date | null | undefined,
): string {
	if (!value) return "";
	const time = new Date(value).getTime();
	if (!Number.isFinite(time)) return "";
	const diffMs = Date.now() - time;
	const minutes = Math.round(diffMs / 60000);
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.round(hours / 24);
	return `${days}d ago`;
}

function ListeningPage() {
	const { user, isPending } = useSession();
	const { tier, isLoading: isSubscriptionLoading } = useSubscription(user?.id);
	const { openProDialog } = useProDialog();
	const dashboardCtx = useDashboardContext();
	const workspaceId = dashboardCtx?.workspace?.id ?? null;

	const [keywordInput, setKeywordInput] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data, isLoading } = db.useQuery(
		workspaceId
			? {
					listeningKeywords: {
						$: { where: { ownerKey: workspaceId } },
					},
					brandMentions: {
						$: {
							where: { ownerKey: workspaceId },
							order: { publishedAt: "desc" },
							limit: 100,
						},
					},
				}
			: null,
	);

	if (isPending || isSubscriptionLoading) {
		return <LoadingScreen />;
	}

	const hasListening = tier === "pro" || tier === "max";

	const keywords = data?.listeningKeywords ?? [];
	const mentions = data?.brandMentions ?? [];
	const maxKeywords = getPlanCapabilities(tier).maxListeningKeywords;
	const atKeywordLimit = keywords.length >= maxKeywords;

	const handleAddKeyword = async () => {
		const keyword = keywordInput.trim();
		if (!keyword || isSubmitting || atKeywordLimit) return;
		setIsSubmitting(true);
		try {
			const response = await fetch("/api/listening/keywords", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ keyword }),
			});
			if (!response.ok) {
				const payload = (await response.json().catch(() => null)) as {
					error?: string;
				} | null;
				toast.error(payload?.error ?? "Failed to add keyword");
				return;
			}
			setKeywordInput("");
			toast.success("Keyword added");
		} catch {
			toast.error("Failed to add keyword");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleRemoveKeyword = async (keywordId: string) => {
		try {
			const response = await fetch("/api/listening/keywords", {
				method: "DELETE",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ keywordId }),
			});
			if (!response.ok) {
				toast.error("Failed to remove keyword");
				return;
			}
			toast.success("Keyword removed");
		} catch {
			toast.error("Failed to remove keyword");
		}
	};

	const handleMarkRead = (mentionId: string) => {
		void db.transact(db.tx.brandMentions[mentionId].update({ isRead: true }));
	};

	return (
		<DashboardLayout>
			<DashboardHeaderSection>
				<DashboardPageHeader
					title="Social Listening"
					description="Monitor Reddit, Bluesky, and YouTube for mentions of your brand and keywords."
				/>
			</DashboardHeaderSection>
			<DashboardContent>
				{!hasListening ? (
					<BrandCard padding="lg">
						<BrandCardHeader>
							<BrandCardTitle>Upgrade to start listening</BrandCardTitle>
							<BrandCardDescription>
								Social listening is included with the Pro and Max plans. Track
								brand mentions across Reddit, Bluesky, and YouTube and get a
								daily digest email.
							</BrandCardDescription>
						</BrandCardHeader>
						<BrandCardContent className="mt-4">
							<BrandButton onClick={() => openProDialog()}>
								Upgrade plan
							</BrandButton>
						</BrandCardContent>
					</BrandCard>
				) : (
					<div className="space-y-6">
						<div className="rounded-xl border border-border bg-card p-5 shadow-sm">
							<div className="flex flex-col gap-1">
								<h2 className="text-base font-semibold text-foreground">
									Keywords ({keywords.length}/{maxKeywords})
								</h2>
								<p className="text-sm text-muted-foreground">
									Add a brand name, product, or competitor to monitor. You can
									track up to {maxKeywords} keywords per workspace. Mentions are
									checked every few hours.
								</p>
							</div>
							<div className="mt-4 flex gap-2">
								<BrandInput
									value={keywordInput}
									onChange={(event) => setKeywordInput(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.preventDefault();
											void handleAddKeyword();
										}
									}}
									placeholder="e.g. AgentReacher"
									maxLength={120}
									disabled={atKeywordLimit}
								/>
								<BrandButton
									onClick={() => void handleAddKeyword()}
									disabled={
										isSubmitting || !keywordInput.trim() || atKeywordLimit
									}
								>
									Add
								</BrandButton>
							</div>

							{atKeywordLimit && (
								<p className="mt-3 text-sm text-muted-foreground">
									You have reached the limit of {maxKeywords} keywords. Remove
									one to add another.
								</p>
							)}

							{keywords.length === 0 ? (
								<p className="mt-4 text-sm text-muted-foreground">
									No keywords yet. Add one above to start monitoring.
								</p>
							) : (
								<ul className="mt-4 flex flex-wrap gap-2">
									{keywords.map((keyword) => (
										<li
											key={keyword.id}
											className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
										>
											<span className="font-medium text-foreground">
												{keyword.keyword}
											</span>
											<button
												type="button"
												aria-label={`Remove ${keyword.keyword}`}
												onClick={() => void handleRemoveKeyword(keyword.id)}
												className="text-muted-foreground transition-colors hover:text-destructive"
											>
												<Trash className="size-4" />
											</button>
										</li>
									))}
								</ul>
							)}
						</div>

						<section className="space-y-3">
							<h2 className="text-lg font-semibold tracking-tight text-foreground">
								Mentions
							</h2>
							{isLoading ? (
								<p className="text-sm text-muted-foreground">
									Loading mentions...
								</p>
							) : mentions.length === 0 ? (
								<div className="rounded-xl border border-dashed border-border bg-card px-6 py-14 text-center">
									<p className="text-sm font-medium text-foreground">
										No mentions yet
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										New mentions appear here after the next check.
									</p>
								</div>
							) : (
								<ul className="grid gap-3">
									{mentions.map((mention) => (
										<li key={mention.id}>
											<div
												className={cn(
													"rounded-xl border border-border bg-card p-5 shadow-sm",
													mention.isRead && "opacity-60",
												)}
											>
												<div className="flex items-center justify-between gap-3">
													<div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
														<span className="rounded bg-muted px-1.5 py-0.5 text-foreground">
															{PLATFORM_LABELS[mention.platform] ??
																mention.platform}
														</span>
														<span>
															{mention.authorName ??
																mention.authorHandle ??
																"Unknown source"}
														</span>
														<span>
															{formatRelativeTime(mention.publishedAt)}
														</span>
													</div>
													<div className="flex items-center gap-3">
														{!mention.isRead && (
															<button
																type="button"
																onClick={() => handleMarkRead(mention.id)}
																className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
															>
																Mark read
															</button>
														)}
														<a
															href={mention.url}
															target="_blank"
															rel="noreferrer"
															aria-label="Open original"
															className="text-muted-foreground transition-colors hover:text-foreground"
														>
															<ArrowSquareOut className="size-4" />
														</a>
													</div>
												</div>
												<div className="mt-2 text-sm text-foreground [&_a]:break-words [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-semibold [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_pre]:text-xs [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
													<Streamdown>{mention.text}</Streamdown>
												</div>
											</div>
										</li>
									))}
								</ul>
							)}
						</section>
					</div>
				)}
			</DashboardContent>
		</DashboardLayout>
	);
}
